import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import ComplianceAlertBanner from '../../components/ui/ComplianceAlertBanner';
import SystemStatusIndicator from '../../components/ui/SystemStatusIndicator';
import QuickActionToolbar from '../../components/ui/QuickActionToolbar';
import VoyageDetailsForm from './components/VoyageDetailsForm';
import FuelConsumptionData from './components/FuelConsumptionData';
import RouteSegments from './components/RouteSegments';
import EmissionsResults from './components/EmissionsResults';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const EmissionsCalculator = () => {
  const [voyageData, setVoyageData] = useState({});
  const [fuelData, setFuelData] = useState({});
  const [routeData, setRouteData] = useState([]);
  const [activeStep, setActiveStep] = useState(1);
  const [isDataComplete, setIsDataComplete] = useState(false);

  const steps = [
    { id: 1, title: 'Voyage Details', icon: 'Ship', component: 'voyage' },
    { id: 2, title: 'Fuel Consumption', icon: 'Fuel', component: 'fuel' },
    { id: 3, title: 'Route Segments', icon: 'Route', component: 'route' },
    { id: 4, title: 'Results', icon: 'Calculator', component: 'results' }
  ];

  useEffect(() => {
    // Check if minimum required data is available
    const hasVoyageData = voyageData?.vesselName && voyageData?.departurePort && voyageData?.arrivalPort;
    const hasFuelData = fuelData?.mainEngineFuelType && fuelData?.mainEngineFuelConsumption;
    const hasRouteData = routeData?.length > 0;
    
    setIsDataComplete(hasVoyageData && hasFuelData && hasRouteData);
  }, [voyageData, fuelData, routeData]);

  const handleVoyageDataChange = (data) => {
    setVoyageData(data);
  };

  const handleFuelDataChange = (data) => {
    setFuelData(data);
  };

  const handleRouteDataChange = (data) => {
    setRouteData(data);
  };

  const handleStepChange = (stepId) => {
    setActiveStep(stepId);
  };

  const handleNextStep = () => {
    if (activeStep < steps?.length) {
      setActiveStep(activeStep + 1);
    }
  };

  const handlePreviousStep = () => {
    if (activeStep > 1) {
      setActiveStep(activeStep - 1);
    }
  };

  const getStepStatus = (stepId) => {
    if (stepId < activeStep) return 'completed';
    if (stepId === activeStep) return 'active';
    return 'pending';
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 1:
        return (
          <VoyageDetailsForm 
            onVoyageDataChange={handleVoyageDataChange}
            voyageData={voyageData}
          />
        );
      case 2:
        return (
          <FuelConsumptionData 
            onFuelDataChange={handleFuelDataChange}
            fuelData={fuelData}
          />
        );
      case 3:
        return (
          <RouteSegments 
            onRouteDataChange={handleRouteDataChange}
            routeData={routeData}
          />
        );
      case 4:
        return (
          <EmissionsResults 
            voyageData={voyageData}
            fuelData={fuelData}
            routeData={routeData}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Emissions Calculator - Carbon Cost Router</title>
        <meta name="description" content="Calculate accurate CO₂e emissions and ETS costs for specific voyages with precise cost allocation based on BIMCO clauses" />
      </Helmet>
      <Header />
      <ComplianceAlertBanner />
      <main className="pt-16">
        {/* Page Header */}
        <div className="bg-card border-b border-border">
          <div className="max-w-7xl mx-auto px-6 py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-foreground">Emissions Calculator</h1>
                <p className="text-muted-foreground mt-1">
                  Calculate CO₂e emissions and ETS costs with BIMCO clause allocation
                </p>
              </div>
              
              <div className="flex items-center space-x-4">
                <SystemStatusIndicator />
                <Button variant="outline" iconName="History" iconPosition="left">
                  Recent Calculations
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="bg-card border-b border-border">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <nav className="flex items-center justify-between">
              {steps?.map((step, index) => (
                <div key={step?.id} className="flex items-center">
                  <button
                    onClick={() => handleStepChange(step?.id)}
                    className={`flex items-center space-x-3 px-4 py-2 rounded-lg transition-maritime ${
                      getStepStatus(step?.id) === 'active' ?'bg-primary text-primary-foreground'
                        : getStepStatus(step?.id) === 'completed' ?'bg-success/10 text-success hover:bg-success/20' :'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      getStepStatus(step?.id) === 'active' ?'bg-primary-foreground text-primary'
                        : getStepStatus(step?.id) === 'completed' ?'bg-success text-success-foreground' :'bg-muted text-muted-foreground'
                    }`}>
                      {getStepStatus(step?.id) === 'completed' ? (
                        <Icon name="Check" size={16} strokeWidth={2} />
                      ) : (
                        <Icon name={step?.icon} size={16} strokeWidth={2} />
                      )}
                    </div>
                    <div className="hidden md:block">
                      <div className="text-sm font-medium">{step?.title}</div>
                      <div className="text-xs opacity-75">Step {step?.id}</div>
                    </div>
                  </button>
                  
                  {index < steps?.length - 1 && (
                    <div className={`hidden md:block w-12 h-0.5 mx-4 ${
                      getStepStatus(step?.id) === 'completed' ? 'bg-success' : 'bg-border'
                    }`} />
                  )}
                </div>
              ))}
            </nav>
          </div>
        </div>

        {/* Content Area */}
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="space-y-6">
            {renderStepContent()}
            
            {/* Navigation Controls */}
            {activeStep < 4 && (
              <div className="flex items-center justify-between pt-6 border-t border-border">
                <Button
                  variant="outline"
                  onClick={handlePreviousStep}
                  disabled={activeStep === 1}
                  iconName="ChevronLeft"
                  iconPosition="left"
                >
                  Previous
                </Button>
                
                <div className="flex items-center space-x-4">
                  <span className="text-sm text-muted-foreground">
                    Step {activeStep} of {steps?.length}
                  </span>
                  <Button
                    onClick={handleNextStep}
                    disabled={activeStep === steps?.length}
                    iconName="ChevronRight"
                    iconPosition="right"
                  >
                    Next Step
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
      <QuickActionToolbar />
    </div>
  );
};

export default EmissionsCalculator;